import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Check } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import {
  desiredColorSettingsAtom,
  roomModeSettingsAtom,
  roomTypeSettingsAtom,
  settingsChangedAtom,
} from '../../atoms/settings';
import type { TYPE_DESIRED_COLOR_OPTIONS } from '../../constants/colors-and-materials';
import { ROOM_MODE_OPTIONS, ROOM_MODE_OPTIONS_WITH_NO_CHECKBOXES } from '../../constants/room-mode';
import { getAvailableColors } from '../../utils/room-specific-options';
import { SettingsSectionHeader } from './settings-section-header';

type ColorChipType = {
  color: TYPE_DESIRED_COLOR_OPTIONS;
  isSelected: boolean;
  onSelect: (color: TYPE_DESIRED_COLOR_OPTIONS) => void;
};

const lightColorValues = ['ivory', 'white', 'soft whites', 'beige', 'linen'];

const ColorChip = ({ color, isSelected, onSelect }: ColorChipType) => {
  const { t } = useTranslation();
  const isLightColor = lightColorValues.includes(color.value);

  return (
    <Button
      type="button"
      variant="ghost"
      onClick={() => onSelect(color)}
      className={cn(
        'h-6 justify-start gap-1 rounded-full border border-slate-200 bg-white px-1.5 text-[10px] font-medium text-slate-600 hover:border-primary-light hover:bg-white',
        isSelected &&
          'border-primary-light bg-primary-light/10 text-slate-700 ring-1 ring-primary-light'
      )}
    >
      <span
        className="flex size-3.5 items-center justify-center rounded-full border border-slate-200"
        style={{ backgroundColor: color.hex }}
      >
        {isSelected ? (
          <Check
            aria-hidden
            className={cn('!size-2.5', isLightColor ? 'text-slate-500' : 'text-white')}
          />
        ) : null}
      </span>
      <span>{t(`colorsObject.${color.translation}`)}</span>
    </Button>
  );
};

export const DesiredColorSettings = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);
  const [colors, setColors] = useAtom(desiredColorSettingsAtom);
  const mode = useAtomValue(roomModeSettingsAtom);
  const roomType = useAtomValue(roomTypeSettingsAtom);
  const setSettingsChanged = useSetAtom(settingsChangedAtom);

  const supportsColors =
    mode === ROOM_MODE_OPTIONS.CHANGE_STYLE.value ||
    mode === ROOM_MODE_OPTIONS.FURNISH_EMPTY_ROOM.value;
  const isDisabled =
    !supportsColors || ROOM_MODE_OPTIONS_WITH_NO_CHECKBOXES.includes(mode) || !roomType;
  const selectedColors = Array.isArray(colors) ? colors : [];
  const colorOptions = getAvailableColors(roomType);

  useEffect(() => {
    if (isDisabled) {
      setIsOpen(false);
      setColors([]);
    }
  }, [isDisabled, setColors]);

  if (!supportsColors) {
    return null;
  }

  const handleSelectColor = (color: TYPE_DESIRED_COLOR_OPTIONS) => {
    const nextColors = selectedColors.includes(color)
      ? selectedColors.filter((selectedColor) => selectedColor !== color)
      : [...selectedColors, color];

    setColors(nextColors);
    setSettingsChanged(true);
  };

  return (
    <div
      className={cn(
        'w-full rounded-sm border border-slate-200 bg-white sm:w-[287px]',
        isDisabled && 'bg-slate-50 text-slate-400'
      )}
    >
      <SettingsSectionHeader
        label={t('desiredColor')}
        count={!isDisabled ? selectedColors.length : undefined}
        isOpen={isOpen}
        disabled={isDisabled}
        onClick={() => setIsOpen((current) => !current)}
      />

      {isOpen && !isDisabled ? (
        <div className="flex flex-wrap gap-1 px-3 pb-3">
          {colorOptions.map((color) => (
            <ColorChip
              key={color.value}
              color={color}
              isSelected={selectedColors.includes(color)}
              onSelect={handleSelectColor}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};
